/**
 * PHI / serialize-no-op filter commits. Never written to the query string.
 * Buckets are keyed by clientId so a missed reset cannot leak values across
 * partners. `useSyncExternalStore` snapshots stay referentially stable when a
 * client has no commits (shared empty object, never a fresh `{}`).
 */
import { registerPatientSessionDataInvalidator } from '@/utilities/auth/patientSession'
import type {
  OrdersListFilterId,
  OrdersListFilterValues,
} from '@/utilities/orders/ordersListFilterValues'

function valuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

const EMPTY_COMMITS: Partial<OrdersListFilterValues> = Object.freeze({})

let committedByClient: Record<string, Partial<OrdersListFilterValues>> = {}
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((listener) => listener())
}

export function getInMemoryFilterCommits(clientId: string): Partial<OrdersListFilterValues> {
  return committedByClient[clientId] ?? EMPTY_COMMITS
}

export function getInMemoryFilterCommit<K extends OrdersListFilterId>(
  clientId: string,
  id: K,
): OrdersListFilterValues[K] | undefined {
  return getInMemoryFilterCommits(clientId)[id]
}

export function subscribeInMemoryFilterCommits(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function setInMemoryFilterCommit<K extends OrdersListFilterId>(
  clientId: string,
  id: K,
  value: OrdersListFilterValues[K],
): void {
  const current = getInMemoryFilterCommits(clientId)
  if (valuesEqual(current[id], value)) return
  committedByClient = {
    ...committedByClient,
    [clientId]: { ...current, [id]: value },
  }
  notify()
}

export function replaceInMemoryFilterCommits(
  clientId: string,
  next: Partial<OrdersListFilterValues>,
): void {
  const current = getInMemoryFilterCommits(clientId)
  if (valuesEqual(current, next)) return
  committedByClient = {
    ...committedByClient,
    [clientId]: { ...next },
  }
  notify()
}

export function resetInMemoryFilterCommits(clientId: string): void {
  if (!(clientId in committedByClient)) return
  const next = { ...committedByClient }
  delete next[clientId]
  committedByClient = next
  notify()
}

export function resetAllInMemoryFilterCommits(): void {
  if (Object.keys(committedByClient).length === 0) return
  committedByClient = {}
  notify()
}

registerPatientSessionDataInvalidator(() => {
  resetAllInMemoryFilterCommits()
})
